import type { BroadsideOrientation, BroadsidePlayer, BroadsideShip, BroadsideShot, BroadsideState, PlayerBroadsideState } from "../types";
import { randomUuid } from "./browser-crypto";

export const BROADSIDE_GRID_SIZE = 10;
export const BROADSIDE_FLEET = [
  { id: "flagship", name: "Flagship", size: 5 },
  { id: "battlecruiser", name: "Battlecruiser", size: 4 },
  { id: "destroyer", name: "Destroyer", size: 3 },
  { id: "submersible", name: "Submersible", size: 3 },
  { id: "patrol", name: "Patrol Boat", size: 2 },
] as const;

const id = (prefix: string) => `${prefix}_${randomUuid()}`;
export const broadsideCellKey = (x: number, y: number) => `${x},${y}`;

export function broadsideShipCells(ship: Pick<BroadsideShip, "x" | "y" | "size" | "orientation">) {
  return Array.from({ length: ship.size }, (_, offset) => ({ x: ship.x + (ship.orientation === "horizontal" ? offset : 0), y: ship.y + (ship.orientation === "vertical" ? offset : 0) }));
}

export function broadsideShipSunk(ship: BroadsideShip) {
  const hits = new Set(ship.hits); return broadsideShipCells(ship).every((cell) => hits.has(broadsideCellKey(cell.x, cell.y)));
}

export function broadsideFleetComplete(player: BroadsidePlayer) {
  return BROADSIDE_FLEET.every((definition) => player.ships.some((ship) => ship.id === definition.id));
}

export function createBroadsideState(now = Date.now()): BroadsideState {
  return {
    gridSize: BROADSIDE_GRID_SIZE,
    players: [
      { id: id("admiral"), name: "Azure Admiral", color: "#4fc7ff", ships: [], ready: false },
      { id: id("admiral"), name: "Crimson Admiral", color: "#ff695f", ships: [], ready: false },
    ],
    phase: "placement", turnNumber: 0, shots: [], updatedAt: now,
  };
}

export function renameBroadsidePlayer(state: BroadsideState, playerId: string, name: string, now = Date.now()) {
  const normalized = name.trim().slice(0, 60); if (!normalized) return state;
  return { ...state, players: state.players.map((player) => player.id === playerId ? { ...player, name: normalized } : player), updatedAt: now };
}

export function placeBroadsideShip(state: BroadsideState, playerId: string, shipId: string, x: number, y: number, orientation: BroadsideOrientation, now = Date.now()): BroadsideState {
  if (state.phase !== "placement" || !Number.isInteger(x) || !Number.isInteger(y) || (orientation !== "horizontal" && orientation !== "vertical")) return state;
  const definition = BROADSIDE_FLEET.find((ship) => ship.id === shipId); const player = state.players.find((candidate) => candidate.id === playerId);
  if (!definition || !player || player.ready) return state;
  const placed: BroadsideShip = { ...definition, x, y, orientation, hits: [] };
  const cells = broadsideShipCells(placed); if (cells.some((cell) => cell.x < 0 || cell.y < 0 || cell.x >= state.gridSize || cell.y >= state.gridSize)) return state;
  const occupied = new Set(player.ships.filter((ship) => ship.id !== shipId).flatMap(broadsideShipCells).map((cell) => broadsideCellKey(cell.x, cell.y)));
  if (cells.some((cell) => occupied.has(broadsideCellKey(cell.x, cell.y)))) return state;
  return { ...state, players: state.players.map((candidate) => candidate.id === playerId ? { ...candidate, ships: [...candidate.ships.filter((ship) => ship.id !== shipId), placed], ready: false } : candidate), updatedAt: now };
}

export function setBroadsidePlayerReady(state: BroadsideState, playerId: string, ready: boolean, now = Date.now()): BroadsideState {
  if (state.phase !== "placement") return state;
  const player = state.players.find((candidate) => candidate.id === playerId); if (!player || (ready && !broadsideFleetComplete(player))) return state;
  const players = state.players.map((candidate) => candidate.id === playerId ? { ...candidate, ready } : candidate);
  const begin = players.length === 2 && players.every((candidate) => candidate.ready);
  return { ...state, players, phase: begin ? "battle" : "placement", activePlayerId: begin ? players[0].id : undefined, turnNumber: begin ? 1 : 0, updatedAt: now };
}

export function fireBroadsideShot(state: BroadsideState, playerId: string, x: number, y: number, now = Date.now()): BroadsideState {
  if (state.phase !== "battle" || state.activePlayerId !== playerId || !Number.isInteger(x) || !Number.isInteger(y) || x < 0 || y < 0 || x >= state.gridSize || y >= state.gridSize) return state;
  const shooter = state.players.find((player) => player.id === playerId); const target = state.players.find((player) => player.id !== playerId); if (!shooter || !target) return state;
  if (state.shots.some((shot) => shot.playerId === playerId && shot.targetPlayerId === target.id && shot.x === x && shot.y === y)) return state;
  const key = broadsideCellKey(x, y); const struck = target.ships.find((ship) => broadsideShipCells(ship).some((cell) => cell.x === x && cell.y === y));
  const players = state.players.map((player) => {
    if (player.id !== target.id || !struck) return player;
    return { ...player, ships: player.ships.map((ship) => ship.id === struck.id ? { ...ship, hits: ship.hits.includes(key) ? ship.hits : [...ship.hits, key] } : ship) };
  });
  const updatedTarget = players.find((player) => player.id === target.id)!; const updatedShip = struck ? updatedTarget.ships.find((ship) => ship.id === struck.id) : undefined;
  const result = !updatedShip ? "miss" : broadsideShipSunk(updatedShip) ? "sunk" : "hit";
  const shot: BroadsideShot = { id: id("salvo"), playerId, targetPlayerId: target.id, x, y, result, shipId: updatedShip?.id, createdAt: now };
  const winner = updatedTarget.ships.length === BROADSIDE_FLEET.length && updatedTarget.ships.every(broadsideShipSunk);
  return { ...state, players, shots: [...state.shots, shot], lastShot: shot, phase: winner ? "finished" : "battle", winnerPlayerId: winner ? playerId : undefined, activePlayerId: winner ? undefined : target.id, turnNumber: state.turnNumber + 1, updatedAt: now };
}

export function resetBroadsideState(state: BroadsideState, now = Date.now()): BroadsideState {
  return { gridSize: state.gridSize || BROADSIDE_GRID_SIZE, players: state.players.slice(0, 2).map((player) => ({ ...player, ships: [], ready: false })), phase: "placement", activePlayerId: undefined, winnerPlayerId: undefined, turnNumber: 0, shots: [], lastShot: undefined, updatedAt: now };
}

export function publicBroadsideState(state: BroadsideState): PlayerBroadsideState {
  return {
    gridSize: state.gridSize,
    players: state.players.map((player) => ({ id: player.id, name: player.name, color: player.color, ready: player.ready, remainingShips: player.ships.filter((ship) => !broadsideShipSunk(ship)).length, sunkShips: player.ships.filter(broadsideShipSunk).map((ship) => structuredClone(ship)) })),
    phase: state.phase, activePlayerId: state.activePlayerId, winnerPlayerId: state.winnerPlayerId, turnNumber: state.turnNumber,
    shots: state.shots.map((shot) => ({ ...shot, shipId: shot.result === "sunk" ? shot.shipId : undefined })),
    lastShot: state.lastShot ? { ...state.lastShot, shipId: state.lastShot.result === "sunk" ? state.lastShot.shipId : undefined } : undefined,
    updatedAt: state.updatedAt,
  };
}
